import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { loadCashfree } from "@/lib/cashfree";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Check, Wallet, AlertTriangle, ExternalLink, Plus, HardDrive, UserPlus, Sparkles } from "lucide-react";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function Billing() {
  const { activeWorkspaceId, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState(null);
  const [addonCatalog, setAddonCatalog] = useState(null);
  const [preview, setPreview] = useState(null);  // R55 · Next invoice preview
  const [cycle, setCycle] = useState("yearly");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    if (!activeWorkspaceId) return;
    const [b, p, ax, cat] = await Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/billing`),
      api.get("/plans"),
      api.get(`/workspaces/${activeWorkspaceId}/addons`),
      api.get(`/workspaces/${activeWorkspaceId}/addons/catalog`),
    ]);
    setData(b.data); setPlans(p.data || []);
    setAddons(ax.data); setAddonCatalog(cat.data);
    // R55 · Preview is admin-only, so a 403 is expected for non-admins.
    try {
      const { data: pv } = await api.get(`/workspaces/${activeWorkspaceId}/billing/next-invoice-preview`);
      setPreview(pv);
    } catch { setPreview(null); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  if (!data) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const currency = data.workspace?.currency || "INR";
  const currentSlug = data.plan?.slug;

  const choose = async (planSlug) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/workspaces/${activeWorkspaceId}/billing/checkout`, {
        plan_slug: planSlug, cycle,
      });
      if (res.contact_sales) {
        toast.info("Reach out at sales@creativeops.io for an Enterprise quote.");
        return;
      }
      if (res.free) {
        toast.success("Switched to free plan");
        await refresh(); load(); return;
      }
      if (res.configured === false) {
        toast.warning("Payment gateway not configured. A platform admin must add Cashfree keys before paid plans can be charged.");
        load(); return;
      }
      // Wire Cashfree JS SDK
      try {
        const cashfree = await loadCashfree(res.mode);
        const checkoutRes = await cashfree.checkout({
          paymentSessionId: res.payment_session_id,
          redirectTarget: "_self",
        });
        if (checkoutRes?.error) {
          toast.error(checkoutRes.error.message || "Checkout cancelled");
          nav(`/app/billing/success?order_id=${res.order_id}`);
        }
      } catch (sdkErr) {
        toast.error("Cashfree SDK failed to load. Order created — visit success page to confirm.");
        nav(`/app/billing/success?order_id=${res.order_id}`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Checkout failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Subscription"
        title="Billing & plans"
        description={`Currency: ${currency} · Cashfree status: ${data.gateway_configured ? data.gateway_mode + " mode (configured)" : "not configured by platform admin"}`}
        testid="billing-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 space-y-8">
        <div className="grid lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2 border border-border p-6">
            <div className="label-eyebrow mb-2">Current plan</div>
            <div className="flex items-baseline gap-3">
              <h2 className="heading-display text-4xl">{data.plan?.name || "Free"}</h2>
              <span className="text-sm text-muted-foreground">
                · {(data.plan?.seat_limit || 1) + (addons?.extra_seats || 0)} seats
                · {(data.plan?.storage_gb || 0.5) + (addons?.extra_storage_gb || 0)} GB
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{data.plan?.description}</p>
            {(addons?.extra_seats > 0 || addons?.extra_storage_gb > 0) && (
              <div className="mt-3 flex gap-2 text-xs">
                {addons.extra_seats > 0 && <span className="chip chip-approved">+{addons.extra_seats} seats add-on</span>}
                {addons.extra_storage_gb > 0 && <span className="chip chip-approved">+{addons.extra_storage_gb} GB add-on</span>}
              </div>
            )}
          </div>
          <div className="border border-border p-6 bg-foreground text-background">
            <Wallet className="w-6 h-6 mb-3" />
            <div className="label-eyebrow opacity-70 mb-1">Add-ons</div>
            <div className="font-bold">Storage & seats</div>
            <p className="text-xs opacity-80 mt-1 mb-3">Pro-rated to your current cycle.</p>
            <div className="flex gap-2">
              <AddonPurchase type="storage" catalog={addonCatalog} onPurchased={load} />
              <AddonPurchase type="seats" catalog={addonCatalog} onPurchased={load} />
            </div>
          </div>
        </div>

        {!data.gateway_configured && (
          <div className="border-2 border-dashed border-yellow-500/40 bg-yellow-500/5 p-4 flex gap-3 items-start" data-testid="gateway-warning">
            <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-bold">Payment gateway not configured</div>
              <div className="text-muted-foreground">A platform Super Admin must add Cashfree keys before paid subscriptions can be charged. Plan changes to <strong>Free</strong> still work instantly.</div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Choose a plan</div>
          <div className="flex items-center gap-3">
            <span className="text-sm">Monthly</span>
            <Switch checked={cycle === "yearly"} onCheckedChange={(v) => setCycle(v ? "yearly" : "monthly")} data-testid="billing-cycle-toggle" />
            <span className="text-sm font-semibold">Yearly <span className="chip chip-approved ml-1">-20%</span></span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
          {plans.map((p) => {
            const k = `${cycle}_price_${currency.toLowerCase()}`;
            const price = p[k];
            const isCurrent = p.slug === currentSlug;
            return (
              <div key={p.slug} className={`border p-5 flex flex-col ${isCurrent ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`plan-card-${p.slug}`}>
                <div className="label-eyebrow mb-2">{p.name}</div>
                <div className="heading-display text-3xl mb-1">{p.slug === "enterprise" ? "Custom" : price === 0 ? "Free" : fmtMoney(price, currency)}</div>
                {price > 0 && p.slug !== "enterprise" && <div className="text-xs text-muted-foreground mb-3">/ {cycle === "yearly" ? "year" : "mo"}</div>}
                <ul className="text-xs space-y-1 mb-4 flex-1">
                  {(p.features || []).slice(0, 4).map((f, i) => (
                    <li key={i} className="flex gap-1.5"><Check className="w-3 h-3 mt-0.5 text-primary shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Button
                  disabled={isCurrent || busy}
                  onClick={() => choose(p.slug)}
                  className="rounded-none w-full text-xs uppercase font-bold tracking-widest"
                  variant={isCurrent ? "outline" : "default"}
                  data-testid={`plan-select-${p.slug}`}
                >
                  {isCurrent ? "Current" : (p.slug === "enterprise" ? "Contact" : "Choose")}
                </Button>
              </div>
            );
          })}
        </div>

        {preview && (
          <div className="border border-border" data-testid="next-invoice-preview">
            <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="label-eyebrow text-[10px]">Next invoice preview</span>
              </div>
              <span className="text-xs text-muted-foreground">
                Period: {fmtDate(preview.period_start)} → {fmtDate(preview.period_end)}
              </span>
            </div>
            <div className="divide-y divide-border">
              {(preview.line_items || []).map((li, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm"
                     data-testid={`preview-line-${li.type}`}>
                  <div className="col-span-8">
                    <div className="font-medium">{li.description}</div>
                    {li.type === "ai_usage" && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {li.calls.toLocaleString()} call{li.calls !== 1 ? "s" : ""} ·
                        {" "}{(li.tokens_in + li.tokens_out).toLocaleString()} tokens ·
                        {" "}est. USD {(li.cost_cents_est_usd / 100).toFixed(2)} · markup ×{li.markup_multiplier}
                      </div>
                    )}
                  </div>
                  <div className="col-span-4 text-right font-mono">
                    {fmtMoney(li.amount, li.currency)}
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-12 gap-2 px-4 py-3 items-center bg-muted/30">
                <div className="col-span-8 label-eyebrow text-[11px]">Estimated total</div>
                <div className="col-span-4 text-right heading-display text-lg" data-testid="preview-total">
                  {fmtMoney(preview.total, preview.currency)}
                </div>
              </div>
            </div>
            {preview.ai_usage_amount > 0 && (
              <div className="px-4 py-2 border-t border-border bg-primary/5 text-[11px] text-muted-foreground">
                AI usage is billed against your Emergent LLM key consumption for the period above.
                It will be automatically added to your next paid invoice.
              </div>
            )}
          </div>
        )}

        {data.invoices?.length > 0 && (
          <div>
            <div className="label-eyebrow mb-3">Invoice history</div>
            <div className="border border-border">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
                <div className="col-span-4">Invoice</div>
                <div className="col-span-3">Date</div>
                <div className="col-span-3">Amount</div>
                <div className="col-span-2">Status</div>
              </div>
              {data.invoices.map((inv) => (
                <div key={inv.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm">
                  <div className="col-span-4 font-mono text-xs">
                    {inv.order_id}
                    {(inv.line_items || []).some((li) => li.type === "ai_usage") && (
                      <span className="chip chip-approved ml-2 text-[9px]" data-testid={`invoice-ai-badge-${inv.id}`}>+ AI usage</span>
                    )}
                  </div>
                  <div className="col-span-3">{fmtDate(inv.created_at)}</div>
                  <div className="col-span-3">{fmtMoney(inv.amount, inv.currency)}</div>
                  <div className="col-span-2"><span className="chip chip-approved">{inv.status}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function AddonPurchase({ type, catalog, onPurchased }) {
  const { activeWorkspaceId } = useAuth();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  if (!catalog) return null;
  const info = catalog[type];
  const total = Math.round(info.prorated_unit_price * qty * 100) / 100;

  const purchase = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/addons/purchase`, { addon_type: type, quantity: Number(qty) });
      toast.success(`Added ${qty} × ${info.unit_label}`);
      setOpen(false);
      onPurchased?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    } finally { setBusy(false); }
  };

  const Icon = type === "storage" ? HardDrive : UserPlus;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none border-white/20 bg-transparent hover:bg-white/10 text-background" data-testid={`addon-${type}-btn`}>
          <Icon className="w-3.5 h-3.5 mr-1.5" />
          {type === "storage" ? "+ Storage" : "+ Seats"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add {info.unit_label}{qty !== 1 ? "s" : ""}</DialogTitle>
          <DialogDescription>
            Pro-rated to your current cycle ({info.days_remaining} of {info.cycle_days} days remaining).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-eyebrow mb-2 inline-block">Quantity</Label>
            <Input type="number" min={1} max={100} value={qty} onChange={(e) => setQty(e.target.value)} className="h-10" data-testid={`addon-${type}-qty`} />
            <div className="text-xs text-muted-foreground mt-2">
              {fmtMoney(info.prorated_unit_price, info.currency)} per {info.unit_label} (prorated)
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-border pt-4">
            <span className="label-eyebrow">Total today</span>
            <span className="heading-display text-2xl">{fmtMoney(total, info.currency)}</span>
          </div>
          <Button onClick={purchase} disabled={busy} className="w-full rounded-none" data-testid={`addon-${type}-purchase`}>
            <Plus className="w-4 h-4 mr-2" /> {busy ? "Purchasing…" : "Confirm purchase"}
          </Button>
          <p className="text-xs text-muted-foreground">
            Add-ons are recorded as a paid invoice immediately (MVP behavior). Renewals will be billed on your next cycle.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
